import { IFText, IFSkeleton } from 'Components'
import Styles from './BalanceTimeline.module.css'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { Waypoint } from 'react-waypoint'
import { Colors } from 'Theme'
import Tooltip from '@mui/material/Tooltip'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import {
  buildBalanceTimeline,
  formatBalanceAmount,
} from 'Utils/BalanceFunctions'

// Audit view of a user's wallet: the balance they hold now at the top, then one
// step per transaction reading backwards, each showing the balance it started
// from, what it moved, and the balance it left behind. Built for disputes —
// "you charged me twice" is answered by reading down the rail.
const BalanceTimeline = ({
  transactions,
  isLoading,
  hasMore,
  onLoadMore,
  maxHeight,
}) => {
  const { t } = useTranslation()
  const entries = useMemo(
    () => buildBalanceTimeline(transactions),
    [transactions],
  )

  if (isLoading)
    return (
      <div className={Styles.Timeline} style={{ maxHeight }}>
        {[0, 1, 2, 3, 4].map((key) => (
          <div className={Styles.SkeletonRow} key={key}>
            <IFSkeleton variant="circular" width="12px" height="12px" />
            <IFSkeleton variant="text" width="100%" height="52px" />
          </div>
        ))}
      </div>
    )

  if (!entries.length)
    return (
      <div className={Styles.Empty}>
        <IFText style={{ color: Colors.DetailPanel }}>
          {t('BalanceTimeline.NoTransactions')}
        </IFText>
      </div>
    )

  const newest = entries[0]
  const currentBalance = newest.recorded
    ? newest.transaction.billingAccountBalance.after
    : null

  return (
    <div className={Styles.Timeline} style={{ maxHeight }}>
      <div className={Styles.Now}>
        <IFText className={Styles.NowLabel}>
          {t('BalanceTimeline.BalanceNow')}
        </IFText>
        <IFText className={Styles.NowValue}>
          {currentBalance === null
            ? t('BalanceTimeline.NotRecorded')
            : formatBalanceAmount(currentBalance, newest.currency)}
        </IFText>
      </div>

      {entries.map((entry) => {
        const { transaction } = entry
        const isCharge = transaction.type === 'charge'
        const movedUp = entry.recorded && entry.delta > 0
        const movementColor = !entry.recorded
          ? Colors.DetailPanel
          : entry.delta === 0
            ? Colors.DetailPanel
            : movedUp
              ? Colors.green
              : Colors.red

        return (
          <React.Fragment key={transaction._id ?? transaction.id}>
            {!entry.continuous && (
              <div className={Styles.Gap}>
                <WarningAmberIcon
                  sx={{ fontSize: '16px', color: Colors.red }}
                />
                <IFText className={Styles.GapText}>
                  {t('BalanceTimeline.UnexplainedGap', {
                    amount: formatBalanceAmount(entry.gap, entry.currency),
                  })}
                </IFText>
              </div>
            )}

            <div className={Styles.Step}>
              <div className={Styles.Rail}>
                <span
                  className={Styles.Node}
                  style={{ background: movementColor }}
                />
                <span className={Styles.Track} />
              </div>

              <div className={Styles.Body}>
                <div className={Styles.Head}>
                  <IFText className={Styles.Type}>
                    {t(
                      `UserPage.TransactionTableColumns.Type.${transaction.type}`,
                    )}
                  </IFText>
                  <IFText
                    className={Styles.Movement}
                    style={{ color: movementColor }}
                  >
                    {entry.recorded
                      ? entry.delta === 0
                        ? t('BalanceTimeline.NoMovement')
                        : `${entry.delta > 0 ? '+' : ''}${formatBalanceAmount(
                            entry.delta,
                            entry.currency,
                          )}`
                      : t('BalanceTimeline.NotRecorded')}
                  </IFText>
                </div>

                <div className={Styles.Meta}>
                  <IFText className={Styles.MetaText}>
                    {moment(transaction.createdAt).format('MMM DD, YYYY LTS')}
                  </IFText>
                  <IFText className={Styles.MetaText}>
                    {isCharge
                      ? transaction.details?.cdrLocation?.name || '-'
                      : transaction.paymentIssuer || '-'}
                  </IFText>
                  <IFText className={Styles.MetaId}>
                    {transaction._id ?? transaction.id}
                  </IFText>
                </div>

                <div className={Styles.Ledger}>
                  {entry.recorded ? (
                    <>
                      <IFText className={Styles.LedgerValue}>
                        {formatBalanceAmount(
                          transaction.billingAccountBalance.before,
                          entry.currency,
                        )}
                      </IFText>
                      <span
                        className={Styles.Arrow}
                        style={{ color: movementColor }}
                      >
                        →
                      </span>
                      <IFText className={Styles.LedgerValue}>
                        {formatBalanceAmount(
                          transaction.billingAccountBalance.after,
                          entry.currency,
                        )}
                      </IFText>
                    </>
                  ) : (
                    <IFText className={Styles.LedgerMissing}>
                      {t('BalanceTimeline.BalanceNotRecorded')}
                    </IFText>
                  )}

                  {entry.settledOnInvoice && (
                    <Tooltip title={t('BalanceTimeline.InvoicedTooltip')}>
                      <span className={Styles.Badge}>
                        <ReceiptLongIcon
                          sx={{ fontSize: '14px', color: Colors.DetailPanel }}
                        />
                        <IFText className={Styles.BadgeText}>
                          {t('BalanceTimeline.Invoiced')}
                        </IFText>
                      </span>
                    </Tooltip>
                  )}

                  {!entry.selfConsistent && (
                    <Tooltip
                      title={t('BalanceTimeline.InconsistentTooltip', {
                        amount: formatBalanceAmount(
                          transaction.isCredit
                            ? Number(transaction.amount)
                            : -Number(transaction.amount),
                          entry.currency,
                        ),
                      })}
                    >
                      <span className={Styles.Badge}>
                        <WarningAmberIcon
                          sx={{ fontSize: '14px', color: Colors.red }}
                        />
                        <IFText
                          className={Styles.BadgeText}
                          style={{ color: Colors.red }}
                        >
                          {t('BalanceTimeline.Inconsistent')}
                        </IFText>
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        )
      })}

      {hasMore && (
        <div className={Styles.WaypointContainer}>
          <Waypoint onEnter={onLoadMore} />
        </div>
      )}
    </div>
  )
}

BalanceTimeline.propTypes = {
  transactions: PropTypes.array,
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default BalanceTimeline
